/**
 * ItemSlot - single item slot (for grids/equipment)
 */

import React, { useEffect, useState } from 'react';
import { RADII, FONT_SIZE, FONT_WEIGHT, COLORS, DURATION, EASING } from '../../theme';
import { ITEM_DATABASE } from '../../../../shared/ItemData';
import { ItemIcon } from '../ItemIcon';
import { Tooltip, createItemTooltip } from '../Tooltip';
import type { ItemTooltipViewerContext } from '../Tooltip';
import {
  ItemData,
  ItemSize,
  SIZE_DIMENSIONS,
  SELECT_BORDER,
  SLOT_ITEM_BG,
  SLOT_EMPTY_BG,
  SLOT_ITEM_BORDER,
  SLOT_EMPTY_BORDER,
  SLOT_HOVER_BG,
  SLOT_INSET_SHADOW,
  SLOT_HOVER_SHADOW_BASE,
  SLOT_HOVER_GLOW_FALLBACK,
  ACCENT,
  rarityColor,
  rarityGlow,
} from './shared';

export interface ItemSlotProps {
  item?: ItemData | null;
  size?: ItemSize;
  selected?: boolean;
  disabled?: boolean;
  emptyIcon?: string;
  onClick?: () => void;
  onRightClick?: () => void;
  onDoubleClick?: () => void;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  draggable?: boolean;
  disableTooltip?: boolean;
  className?: string;
  ariaLabel?: string;
  viewer?: ItemTooltipViewerContext;
}

const quantityBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 2,
  right: 2,
  background: 'rgba(0, 0, 0, 0.8)',
  color: COLORS.textPrimary,
  fontSize: FONT_SIZE.xs,
  fontWeight: FONT_WEIGHT.bold,
  padding: '1px 4px',
  borderRadius: RADII.sm,
  minWidth: 16,
  textAlign: 'center',
};

const levelBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  top: 2,
  left: 2,
  background: 'rgba(0, 0, 0, 0.7)',
  color: ACCENT,
  fontSize: 9,
  fontWeight: FONT_WEIGHT.bold,
  padding: '1px 3px',
  borderRadius: RADII.sm,
};

const dragBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 1,
  left: 3,
  color: 'rgba(255, 255, 255, 0.72)',
  fontSize: 11,
  lineHeight: 1,
  textShadow: '0 1px 2px rgba(0, 0, 0, 0.9)',
  pointerEvents: 'none',
};

export const ItemSlot: React.FC<ItemSlotProps> = ({
  item,
  size = 'md',
  selected = false,
  disabled = false,
  emptyIcon = '□',
  onClick,
  onRightClick,
  onDoubleClick,
  onDragStart,
  onDragEnd,
  draggable = false,
  disableTooltip = false,
  className,
  ariaLabel,
  viewer,
}) => {
  const dimensions = SIZE_DIMENSIONS[size];
  const hasItem = !!item;
  const rarity = item?.rarity;
  const glow = rarityGlow(rarity);
  const hasCanonicalThumbnail = !!item
    && Object.prototype.hasOwnProperty.call(ITEM_DATABASE, item.id);
  const hasImageIcon = typeof item?.icon === 'string' && item.icon.startsWith('/');
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  useEffect(() => {
    setImageLoadFailed(false);
  }, [item?.icon]);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRightClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!hasItem || disabled) return;
    if (e.key === 'Enter' && (onRightClick || onClick)) {
      e.preventDefault();
      (onRightClick ?? onClick)?.();
    } else if (e.key === ' ' && onClick) {
      e.preventDefault();
      onClick();
    } else if ((e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) && onRightClick) {
      e.preventDefault();
      onRightClick();
    }
  };

  const interactiveHover = hasItem && !disabled && hovered;

  const borderColor = selected
    ? SELECT_BORDER
    : rarityColor(rarity) ?? (hasItem ? `var(--item-slot-border, ${SLOT_ITEM_BORDER})` : `var(--item-slot-empty-border, ${SLOT_EMPTY_BORDER})`);

  let boxShadow = glow ?? SLOT_INSET_SHADOW;
  if (interactiveHover) {
    boxShadow = `${SLOT_HOVER_SHADOW_BASE}, ${glow ?? SLOT_HOVER_GLOW_FALLBACK}`;
  }

  let transform = 'none';
  if (interactiveHover) {
    transform = active ? 'translateY(0)' : 'translateY(-2px)';
  }

  const containerStyle: React.CSSProperties = {
    width: dimensions.width,
    height: dimensions.height,
    background: interactiveHover
      ? `var(--item-slot-hover-bg, ${SLOT_HOVER_BG})`
      : hasItem
        ? `var(--item-slot-bg, ${SLOT_ITEM_BG})`
        : `var(--item-slot-empty-bg, ${SLOT_EMPTY_BG})`,
    border: `2px solid ${borderColor}`,
    borderRadius: RADII.lg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    cursor: disabled ? 'not-allowed' : hasItem ? 'pointer' : 'default',
    transition: `all ${DURATION.base} ${EASING.standard}`,
    opacity: disabled ? 0.5 : 1,
    boxShadow,
    transform,
  };

  const iconStyle: React.CSSProperties = {
    fontSize: Math.max(dimensions.width * 0.5, 14),
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
    opacity: hasItem ? 1 : 0.3,
  };

  const slot = (
    <div
      style={containerStyle}
      onClick={onClick}
      onContextMenu={handleContextMenu}
      onDoubleClick={onDoubleClick}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      draggable={draggable}
      data-draggable={draggable && hasItem ? 'true' : 'false'}
      className={className}
      aria-label={ariaLabel ?? (item ? `${item.name} item` : undefined)}
      role={hasItem && onClick ? 'button' : undefined}
      tabIndex={hasItem && !disabled ? 0 : undefined}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => { setHovered(false); setActive(false); }}
    >
      <div style={iconStyle}>
        {hasCanonicalThumbnail && item ? (
          <ItemIcon itemId={item.id} size={Math.max(Math.min(dimensions.width, dimensions.height) - 8, 16)} />
        ) : hasImageIcon && item && !imageLoadFailed ? (
          <img
            src={item.icon}
            alt=""
            aria-hidden="true"
            draggable={false}
            onError={() => setImageLoadFailed(true)}
            style={{
              display: 'block',
              width: Math.max(dimensions.width - 8, 16),
              height: Math.max(dimensions.height - 8, 16),
              objectFit: 'contain',
            }}
          />
        ) : (
          hasImageIcon ? '📦' : item?.icon || (item ? '📦' : emptyIcon)
        )}
      </div>

      {item && item.quantity && item.quantity > 1 && (
        <div style={quantityBadgeStyle}>{item.quantity}</div>
      )}

      {item && item.level && item.level > 0 && (
        <div style={levelBadgeStyle}>Lv.{item.level}</div>
      )}

      {item && draggable && (
        <div style={dragBadgeStyle} aria-hidden="true" title="Drag item">⠿</div>
      )}
    </div>
  );

  if (!item || disableTooltip) return slot;

  return (
    <Tooltip data={createItemTooltip(item, viewer, draggable ? { actions: 'bag' } : {})} position="top">
      {slot}
    </Tooltip>
  );
};
